import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Input, 
  Progress, 
  Tooltip, 
  Button, 
  Space, 
  Badge, 
  Tabs, 
  Statistic, 
  Row, 
  Col,
  Select,
  Modal,
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  ClockCircleOutlined, 
  LoadingOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  PhoneOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  UserOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import { employeeService } from '../../../services';
import { formatMexicoTime } from '../../../utils/timeUtils';
import type { PhoneRecord, UploadTask, ManualEntry, ManualEntryStats } from '../../../types';

const { Search } = Input;

const MyUploadsPage: React.FC = () => {
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const [records, setRecords] = useState<PhoneRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<PhoneRecord[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<UploadTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'tasks' | 'records' | 'manual' | 'stats'>('manual');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 上传任务分页相关状态
  const [taskPage, setTaskPage] = useState(1);
  const [taskPagination, setTaskPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  });

  // 手动入库相关状态
  const [manualEntries, setManualEntries] = useState<ManualEntry[]>([]);
  const [filteredManualEntries, setFilteredManualEntries] = useState<ManualEntry[]>([]);
  const [manualStats, setManualStats] = useState<ManualEntryStats | null>(null);
  const [manualPage, setManualPage] = useState(1);
  const [manualPageSize, setManualPageSize] = useState(10);
  const [manualTotal, setManualTotal] = useState(0);
  const [manualDatasetFilter, setManualDatasetFilter] = useState<number | undefined>();

  // 重复号码详情 Modal 相关状态
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UploadTask | null>(null);

  const fetchData = async (taskPageNum: number = taskPage) => {
    try {
      setLoading(true);
      const [uploadsResponse, manualEntriesResponse, statsResponse] = await Promise.all([
        employeeService.getMyUploads({ 
          page: taskPageNum, 
          per_page: 10 
        }),
        employeeService.getManualEntries({ page: manualPage, per_page: manualPageSize, dataset_id: manualDatasetFilter }),
        employeeService.getManualEntryStats(),
      ]);
      
      setUploadTasks(uploadsResponse.upload_tasks || []);
      setFilteredTasks(uploadsResponse.upload_tasks || []);
      setRecords(uploadsResponse.records || []);
      setFilteredRecords(uploadsResponse.records || []);
      
      // 更新上传任务分页信息
      if (uploadsResponse.pagination) {
        setTaskPagination(uploadsResponse.pagination);
      }
      
      setManualEntries(manualEntriesResponse.entries || []);
      setFilteredManualEntries(manualEntriesResponse.entries || []);
      setManualTotal(manualEntriesResponse.total || 0);
      setManualStats(statsResponse);
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 启动轮询
  const startPolling = () => {
    // 如果已经在轮询中，不要重复启动
    if (intervalRef.current) {
      return;
    }
    intervalRef.current = setInterval(() => {
      fetchData();
    }, 3000); // 每3秒轮询一次
  };

  // 停止轮询
  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // 初始化数据获取
  useEffect(() => {
    fetchData();
    
    return () => {
      stopPolling();
    };
  }, []);

  // 监控任务状态变化，决定是否开启轮询
  useEffect(() => {
    const processingTasks = uploadTasks.filter(task => 
      task.status === 'pending' || task.status === 'processing'
    );
    
    if (processingTasks.length > 0) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [uploadTasks]);

  const handleSearch = (value: string) => {
    if (!value) {
      setFilteredRecords(records);
      setFilteredTasks(uploadTasks);
      setFilteredManualEntries(manualEntries);
    } else {
      // 搜索记录
      const filteredRecords = records.filter(record =>
        record.phone_number.includes(value) ||
        record.dataset_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredRecords(filteredRecords);
      
      // 搜索任务
      const filteredTasks = uploadTasks.filter(task =>
        task.filename.toLowerCase().includes(value.toLowerCase()) ||
        task.dataset_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredTasks(filteredTasks);

      // 搜索手动入库记录
      const filteredManual = manualEntries.filter(entry =>
        entry.phone_number.includes(value) ||
        entry.dataset_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredManualEntries(filteredManual);
    }
  };

  // 获取状态显示信息
  const getStatusDisplay = (status: UploadTask['status']) => {
    const statusMap = {
      'pending': { 
        text: '等待处理', 
        color: 'warning', 
        icon: <ClockCircleOutlined /> 
      },
      'processing': { 
        text: '处理中', 
        color: 'processing', 
        icon: <LoadingOutlined spin /> 
      },
      'completed': { 
        text: '已完成', 
        color: 'success', 
        icon: <CheckCircleOutlined /> 
      },
      'failed': { 
        text: '处理失败', 
        color: 'error', 
        icon: <CloseCircleOutlined /> 
      }
    };
    
    return statusMap[status] || statusMap['pending'];
  };

  // 任务列表表格列定义
  const taskColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
    },
    {
      title: '数据集',
      dataIndex: 'dataset_name',
      key: 'dataset_name',
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: UploadTask['status']) => {
        const statusInfo = getStatusDisplay(status);
        return (
          <Badge 
            status={statusInfo.color as any} 
            text={
              <span>
                {statusInfo.icon}
                <span style={{ marginLeft: 4 }}>{statusInfo.text}</span>
              </span>
            } 
          />
        );
      },
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: UploadTask) => (
        <div style={{ width: 120 }}>
          <Progress 
            percent={progress} 
            size="small" 
            status={record.status === 'failed' ? 'exception' : undefined}
            showInfo={false}
          />
          <span style={{ fontSize: 12, color: '#666' }}>{progress}%</span>
        </div>
      ),
    },
    {
      title: '处理结果',
      key: 'result',
      render: (record: UploadTask) => {
        if (record.status === 'completed') {
          return (
            <div style={{ fontSize: 12 }}>
              <div>总行数: {record.total_lines}</div>
              <div>有效: {record.valid_phones}, 无效: {record.invalid_phones}</div>
              <div style={{ color: 'green' }}>新增: {record.new_phones_added}</div>
            </div>
          );
        } else if (record.status === 'failed') {
          return (
            <Tooltip title={record.error_message}>
              <Tag color="red">失败</Tag>
            </Tooltip>
          );
        }
        return '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatMexicoTime(date),
    },
    {
      title: '操作',
      key: 'action',
      width: 0,
      render: (record: UploadTask) => {
        // 只有当任务完成且有重复号码详情时才显示按钮
        if (record.status === 'completed' && record.duplicate_details && record.duplicate_details.length > 0) {
          return (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedTask(record);
                setDuplicateModalVisible(true);
              }}
            >
              重复号码详情
            </Button>
          );
        }
        return '-';
      },
    },
  ];

  // 记录列表表格列定义
  const recordColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '电话号码',
      dataIndex: 'phone_number',
      key: 'phone_number',
    },
    {
      title: '所属数据集',
      dataIndex: 'dataset_name',
      key: 'dataset_name',
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: '上传时间',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
      render: (date: string) => formatMexicoTime(date),
    },
  ];

  // 手动入库记录表格列定义
  const manualEntryColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '电话号码',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone: string) => (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ),
    },
    {
      title: '所属数据集',
      dataIndex: 'dataset_name',
      key: 'dataset_name',
      render: (name: string) => <Tag color="green">{name}</Tag>,
    },
    {
      title: '入库时间',
      dataIndex: 'entered_at',
      key: 'entered_at',
      render: (date: string) => formatMexicoTime(date),
    },
  ];

  // 生成统计图表数据
  const getChartData = () => {
    if (!manualStats) return [];
    
    const data = [];
    const currentWeekData = manualStats.current_week.daily_data;
    const lastWeekData = manualStats.last_week.daily_data;

    for (let i = 0; i < 7; i++) {
      const currentDay = currentWeekData[i];
      const lastDay = lastWeekData[i];
      
      data.push({
        day: currentDay.day_name,
        week: '本周',
        count: currentDay.count,
      });
      
      data.push({
        day: lastDay.day_name,
        week: '上周',
        count: lastDay.count,
      });
    }
    
    return data;
  };

  const chartConfig = {
    data: getChartData(),
    xField: 'day',
    yField: 'count',
    seriesField: 'week',
    smooth: true,
    point: {
      size: 5,
      shape: 'diamond',
    },
    color: ['#1890ff', '#ff7875'],
  };

  return (
    <div>
      <Card
        title="我的数据管理"
        extra={
          <Space>
            <Search
              placeholder={
                activeTab === 'tasks' ? "搜索文件名或数据集" : 
                activeTab === 'manual' ? "搜索手动入库记录" : 
                "搜索电话号码或数据集名称"
              }
              allowClear
              onSearch={handleSearch}
              style={{ width: 300 }}
              enterButton={<SearchOutlined />}
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchData()}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as any)}
          items={[
            {
              key: 'manual',
              label: `手动入库 (${manualTotal})`,
              children: (
                <Table
                  columns={manualEntryColumns}
                  dataSource={filteredManualEntries}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={{
                    current: manualPage,
                    total: manualTotal,
                    pageSize: manualPageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
                    onChange: (page) => {
                      setManualPage(page);
                      // 重新获取数据
                      fetchData();
                    },
                    onShowSizeChange: (current, size) => {
                      setManualPage(1);
                      setManualPageSize(size);
                      fetchData();
                    },
                  }}
                />
              ),
            },
            {
              key: 'tasks',
              label: `上传任务 (${taskPagination.total})`,
              children: (
                <Table
                  columns={taskColumns}
                  dataSource={filteredTasks}
                  rowKey="id"
                  loading={loading}
                  scroll={{ x: 1200 }}
                  pagination={{
                    current: taskPagination.page,
                    total: taskPagination.total,
                    pageSize: taskPagination.per_page,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `第 ${range[0]}-${range[1]} 条，共 ${total} 条任务`,
                    onChange: (page, pageSize) => {
                      setTaskPage(page);
                      fetchData(page);
                    },
                    onShowSizeChange: (current, size) => {
                      setTaskPage(1);
                      fetchData(1);
                    },
                  }}
                />
              ),
            },
            // 隐藏的功能标签页 - 保留代码但不显示
            // {
            //   key: 'stats',
            //   label: '手动报表',
            //   children: manualStats ? (
            //     <div>
            //       <Row gutter={16} style={{ marginBottom: 24 }}>
            //         <Col span={8}>
            //           <Card>
            //             <Statistic
            //               title="本周入库总数"
            //               value={manualStats.current_week.total}
            //               prefix={<PhoneOutlined />}
            //               valueStyle={{ color: '#3f8600' }}
            //             />
            //           </Card>
            //         </Col>
            //         <Col span={8}>
            //           <Card>
            //             <Statistic
            //               title="上周入库总数"
            //               value={manualStats.last_week.total}
            //               prefix={<PhoneOutlined />}
            //               valueStyle={{ color: '#cf1322' }}
            //             />
            //           </Card>
            //         </Col>
            //         <Col span={8}>
            //           <Card>
            //             <Statistic
            //               title="周环比变化"
            //               value={Math.abs(manualStats.comparison.change_percent)}
            //               precision={1}
            //               suffix="%"
            //               prefix={
            //                 manualStats.comparison.change > 0 ? 
            //                 <ArrowUpOutlined /> : 
            //                 manualStats.comparison.change < 0 ? 
            //                 <ArrowDownOutlined /> : 
            //                 <MinusOutlined />
            //               }
            //               valueStyle={{
            //                 color: manualStats.comparison.change > 0 ? '#3f8600' : 
            //                        manualStats.comparison.change < 0 ? '#cf1322' : '#666'
            //               }}
            //             />
            //           </Card>
            //         </Col>
            //       </Row>
            //       
            //       <Card title="手动入库统计对比" style={{ marginBottom: 24 }}>
            //         <Line {...chartConfig} />
            //       </Card>
            //     </div>
            //   ) : (
            //     <div style={{ textAlign: 'center', padding: 50 }}>
            //       <p>暂无统计数据</p>
            //     </div>
            //   ),
            // },
            // {
            //   key: 'records',
            //   label: `上传记录 (${records.length})`,
            //   children: (
            //     <Table
            //       columns={recordColumns}
            //       dataSource={filteredRecords}
            //       rowKey="id"
            //       loading={loading}
            //       pagination={{
            //         pageSize: 20,
            //         showSizeChanger: true,
            //         showQuickJumper: true,
            //         showTotal: (total, range) =>
            //           `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            //       }}
            //     />
            //   ),
            // },
          ]}
        />
      </Card>

      {/* 重复号码详情 Modal */}
      <Modal
        title={
          <div>
            <EyeOutlined style={{ marginRight: 8 }} />
            重复号码详情
            {selectedTask && selectedTask.duplicate_details && (
              <span style={{ marginLeft: 8, fontSize: 14, color: '#666' }}>
                (共 {selectedTask.duplicate_details.length} 个重复号码)
              </span>
            )}
          </div>
        }
        open={duplicateModalVisible}
        onCancel={() => {
          setDuplicateModalVisible(false);
          setSelectedTask(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setDuplicateModalVisible(false);
              setSelectedTask(null);
            }}
          >
            关闭
          </Button>,
        ]}
        width={800}
      >
        {selectedTask && selectedTask.duplicate_details && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <div style={{ fontSize: 14 }}>
                <strong>任务信息：</strong>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                <div>文件名: {selectedTask.filename}</div>
                <div>数据集: <Tag color="blue">{selectedTask.dataset_name}</Tag></div>
                <div>上传者: {selectedTask.uploader}</div>
              </div>
            </div>

            <Table
              size="small"
              dataSource={selectedTask.duplicate_details}
              columns={[
                {
                  title: '序号',
                  key: 'index',
                  width: 60,
                  render: (_: any, __: any, index: number) => index + 1,
                },
                {
                  title: '号码',
                  dataIndex: 'phone',
                  key: 'phone',
                  width: 150,
                  render: (phone: string) => (
                    <Space>
                      <PhoneOutlined />
                      <strong>{phone}</strong>
                    </Space>
                  ),
                },
                {
                  title: '已被上传者',
                  dataIndex: 'uploader',
                  key: 'uploader',
                  width: 140,
                  render: (uploader: string) => (
                    <Space>
                      <UserOutlined />
                      <Tag color="orange">{uploader}</Tag>
                    </Space>
                  ),
                },
                {
                  title: '上传时间',
                  dataIndex: 'uploaded_at',
                  key: 'uploaded_at',
                  render: (time: string) => formatMexicoTime(time),
                },
              ]}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 个重复号码`,
              }}
              rowKey="phone"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MyUploadsPage;
